import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  VStack,
  Text,
  HStack,
  Image,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { OdmaUrlType } from "../../enums/OdmaUrlType";
import { useToast } from "../../hooks/useToast";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { LandingPageConfigurationForm } from "../../forms/LandingPageConfigurationForm";

interface LandingPageConfigurationDetailProps {
  create?: boolean;
}

export const LandingPageConfigurationDetail = (
  props: LandingPageConfigurationDetailProps
) => {
  const [searchParams] = useSearchParams();
  const { businessId, landingPageConfigurationId } = useParams();
  const [landingPageConfiguration, setLandingPageConfiguration] =
    useState<CherryHubAdminApi.LandingPageConfigurationItem | null>();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    data: landingPageConfigurationDoc,
    isLoading,
    error: landingPageConfigurationError,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !landingPageConfigurationId) {
        return null;
      }

      return apiClient.getLandingPageConfiguration(
        businessId,
        landingPageConfigurationId
      );
    },
    [businessId, landingPageConfigurationId]
  );

  useEffect(() => {
    setLandingPageConfiguration(landingPageConfigurationDoc);
  }, [landingPageConfigurationDoc]);

  if (landingPageConfigurationError)
    return <ErrorMessage>{landingPageConfigurationError}</ErrorMessage>;

  const onSuccess = useCallback(
    (
      landingPageConfiguration: CherryHubAdminApi.LandingPageConfigurationItem
    ) => {
      setLandingPageConfiguration(landingPageConfiguration);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Landing Page Configuration created.");
        navigate(
          `/admin/businesses/${businessId}/landing-page-configurations/${landingPageConfiguration.Meta?.Id}`
        );
      } else {
        showSuccessToast("Landing Page Configuration updated.");
      }
    },
    [showSuccessToast, landingPageConfiguration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Landing Page Configuration update failed.");
    },
    [showErrorToast]
  );

  const onCancel = () => {
    props.create
      ? navigate(`/admin/businesses/${businessId}/landing-page-configurations`)
      : setEditMode(false);
  };

  const { data: odmaUrl, isLoading: isOdmaUrlLoading } = useAdminApiRequest(
    (adminApiClient) => {
      if (!businessId || !landingPageConfigurationId) {
        return null;
      }

      return adminApiClient.getOdmaUrl(
        businessId!,
        OdmaUrlType.LandingPage,
        landingPageConfigurationId!
      );
    },
    [businessId, landingPageConfigurationId]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Landing Page Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" w="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/landing-page-configurations`}
              >
                Landing Page Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/landing-page-configurations/${
                  props.create ? "create" : landingPageConfiguration?.id
                }`}
              >
                {props.create ? "New" : landingPageConfiguration?.FriendlyName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || isOdmaUrlLoading || !landingPageConfiguration) &&
            !props.create && (
              <VStack
                w="100%"
                p={6}
                boxShadow="lg"
                alignItems="flex-start"
                spacing={4}
              >
                <HStack w="100%" justifyContent="flex-start" spacing={4}>
                  <Box flexGrow={1}>
                    <Skeleton w="320px" height="40px" />
                  </Box>
                  {!editMode && <Skeleton w="108px" height="40px" />}
                  <Skeleton w="92px" height="40px" />
                  <Skeleton w="80px" height="40px" />
                </HStack>
                <VStack w="100%" spacing={6} alignItems="flex-start">
                  <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
                  <Skeleton w="100%" height="40px" />
                  <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
                  <Skeleton w="100%" height="40px" />
                </VStack>
              </VStack>
            )}

          {!isLoading && !isOdmaUrlLoading && editMode && (
            <LandingPageConfigurationForm
              businessId={businessId!}
              landingPageConfiguration={landingPageConfiguration}
              onSuccess={onSuccess}
              onFailure={onFailure}
              onCancel={onCancel}
            />
          )}

          {!isLoading &&
            !isOdmaUrlLoading &&
            !editMode &&
            landingPageConfiguration && (
              <>
                <HStack w="100%">
                  <Box flexGrow={2} pb={4}>
                    <CopyText
                      text={landingPageConfiguration?.Meta?.Id!}
                      hover
                      adjustMargin
                    >
                      <Text fontSize="md" color="cherryUi.600">
                        {landingPageConfiguration?.id} /{" "}
                        {landingPageConfiguration?.LandingPageConfigurationNo}
                      </Text>
                    </CopyText>
                    <Text fontSize="xl" color="cherryUi.600">
                      <b>{landingPageConfiguration?.FriendlyName}</b>
                    </Text>
                  </Box>

                  <Box>
                    {odmaUrl?.Value != null && (
                      <PreviewButton
                        url={odmaUrl?.Value as string}
                        variant="cherryButton"
                      >
                        Open
                      </PreviewButton>
                    )}
                  </Box>

                  <Box>
                    {odmaUrl?.Value != null && (
                      <CopyText
                        text={odmaUrl?.Value as string}
                        variant="cherryButton"
                      >
                        Copy Url
                      </CopyText>
                    )}
                  </Box>

                  <Box>
                    <BusinessMenu
                      businessId={businessId!}
                      onClick={() => setEditMode(true)}
                    />
                  </Box>
                </HStack>

                <HStack w="100%" pb={6}>
                  <VStack alignItems="flex-start" w="100%">
                    <ViewControl
                      label="Enabled"
                      value={landingPageConfiguration?.IsEnabled ? "Yes" : "No"}
                    />

                    <ViewControl
                      label="Redirect URL"
                      value={landingPageConfiguration.RedirectUrl ?? "-"}
                    />

                    <ViewControl
                      label="Check Code"
                      value={landingPageConfiguration?.CheckCode ?? "-"}
                    />

                    <ViewControl
                      label="Page Title"
                      value={landingPageConfiguration?.PageTitle ?? "-"}
                    />

                    <ViewControl label="Privacy Policy URL">
                      <Box flex={3}>
                        {(landingPageConfiguration?.PrivacyPolicyUrl && (
                          <PreviewButton
                            url={`${landingPageConfiguration?.PrivacyPolicyUrl}`}
                            adjustMargin
                          >
                            {landingPageConfiguration?.PrivacyPolicyUrl}
                          </PreviewButton>
                        )) ??
                          "-"}
                      </Box>
                    </ViewControl>

                    <ViewControl label="Terms and Conditions URL">
                      <Box flex={3}>
                        {(landingPageConfiguration?.TermsConditionsUrl && (
                          <PreviewButton
                            url={`${landingPageConfiguration?.TermsConditionsUrl}`}
                            adjustMargin
                          >
                            {landingPageConfiguration?.TermsConditionsUrl}
                          </PreviewButton>
                        )) ??
                          "-"}
                      </Box>
                    </ViewControl>
                  </VStack>

                  {landingPageConfiguration?.LogoFileUrl && (
                    <Box alignSelf="flex-start">
                      <Image
                        maxHeight="200px"
                        objectFit="contain"
                        src={landingPageConfiguration?.LogoFileUrl}
                      />
                    </Box>
                  )}
                </HStack>

                <Accordion
                  w="100%"
                  defaultIndex={[0, 1, 2, 3]}
                  allowToggle
                  allowMultiple
                  variant="section"
                >
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Page HTML Content</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <Box
                              width="100%"
                              borderWidth="1px"
                              borderRadius="lg"
                              boxShadow="lg"
                              overflowX="auto"
                              padding={6}
                              flex={3}
                              fontSize="sm"
                              dangerouslySetInnerHTML={{
                                __html:
                                  landingPageConfiguration?.PageHtmlContent ??
                                  "",
                              }}
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>

                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Theme Data</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack w="100%" alignItems="flex-start">
                            <Box
                              width="100%"
                              borderWidth="1px"
                              borderRadius="lg"
                              boxShadow="lg"
                              overflowX="auto"
                              padding={6}
                              flex={3}
                            >
                              {landingPageConfiguration?.ThemeData ? (
                                <pre>
                                  {JSON.stringify(
                                    landingPageConfiguration?.ThemeData,
                                    null,
                                    2
                                  )}
                                </pre>
                              ) : (
                                ""
                              )}
                            </Box>
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              </>
            )}
        </VStack>
      </PageContent>
    </>
  );
};
